import { useParams, Link } from 'react-router-dom';
import { getDramaById, getSimilarDramas, screenshotImages } from '@/mocks/dramas';
import NotFound from '@/pages/NotFound';

export default function DramaPage() {
  const { id } = useParams();
  const dramaId = Number(id);
  const drama = getDramaById(dramaId);

  if (!drama) {
    return <NotFound />;
  }

  const similar = getSimilarDramas(dramaId, 4);
  const screenshots = [
    screenshotImages[dramaId % screenshotImages.length],
    screenshotImages[(dramaId + 1) % screenshotImages.length],
    screenshotImages[(dramaId + 2) % screenshotImages.length],
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-[72px] max-w-[1400px] mx-auto">
            <Link to="/" className="flex items-center gap-2 text-foreground-950 no-underline cursor-pointer">
              <span className="w-9 h-9 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg font-heading text-lg">努</span>
              <span className="text-xl font-heading font-bold whitespace-nowrap">努努影院</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/category" className="text-sm text-foreground-600 hover:text-primary-500 cursor-pointer">分类浏览</Link>
              <Link to="/search" className="text-sm text-foreground-600 hover:text-primary-500 cursor-pointer">搜索</Link>
            </div>
          </div>
        </div>
      </header>

      <div className="pt-[72px]">
        <div className="w-full px-4 md:px-6 max-w-[1200px] mx-auto py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 w-full lg:w-[320px]">
              <div className="w-full aspect-[3/4] rounded-2xl overflow-hidden border border-background-200">
                <img
                  src={drama.image}
                  alt={drama.name}
                  className="w-full h-full object-cover object-top"
                  onError={(e) => { const t = e.currentTarget; t.style.display = 'none'; }}
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">{drama.name}</h1>

              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-sm font-medium rounded-full whitespace-nowrap">{drama.region}</span>
                <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-sm font-medium rounded-full whitespace-nowrap">{drama.year}</span>
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full whitespace-nowrap">{drama.genre}</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 text-sm font-medium rounded-full whitespace-nowrap flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  {drama.rating}
                </span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 text-sm font-medium rounded-full whitespace-nowrap">{drama.episodes}集</span>
              </div>

              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">{drama.description}</p>

              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap">
                  <i className="ri-play-circle-line text-lg"></i>
                  立即播放
                </span>
                <span className="inline-flex items-center gap-2 px-6 py-3 border border-background-300 text-foreground-700 rounded-full font-medium text-sm cursor-pointer hover:border-primary-300 hover:text-primary-500 transition-colors whitespace-nowrap">
                  <i className="ri-heart-line text-lg"></i>
                  收藏
                </span>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">精彩截图</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {screenshots.map((img, idx) => (
                <div key={idx} className="relative w-full aspect-video rounded-xl overflow-hidden border border-background-200">
                  <img
                    src={img}
                    alt={`${drama.name} 截图 ${idx + 1}`}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    onError={(e) => { const t = e.currentTarget; t.style.display = 'none'; }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">相似推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {similar.map((d) => (
                <Link
                  key={d.id}
                  to={`/drama/${d.id}`}
                  className="group bg-background-100 border border-background-200 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md cursor-pointer no-underline"
                >
                  <div className="relative w-full aspect-[3/4] overflow-hidden">
                    <img
                      src={d.image}
                      alt={d.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { const t = e.currentTarget; t.style.display = 'none'; }}
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-bold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">{d.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-xs text-foreground-400">{d.rating}分</span>
                      <span className="text-xs text-foreground-400">{d.year}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}